import { encode } from 'windows-1251'

class SerialWrapper {
  constructor (serialPortOptions = {}) {
    this.defaultOptions = {
      baudRate: 9600,
      dataBits: 8,
      parity: 'none',
      stopBits: 1,
      autoOpen: true
    }

    this.port = null
    this.reader = null
    this.portOpened = false
    this.commandPromise = null
    this.messageStarted = false
    this.messageBytes = []
    this.statusCodes = this.getStatusCodes()
    this.printerPortKey = serialPortOptions.printerPortKey

    if (serialPortOptions.baudRate !== undefined) {
      this.defaultOptions.baudRate = serialPortOptions.baudRate
    }
    if (serialPortOptions.dataBits !== undefined) {
      this.defaultOptions.dataBits = serialPortOptions.dataBits
    }
    if (serialPortOptions.parity !== undefined) {
      this.defaultOptions.parity = serialPortOptions.parity
    }
    if (serialPortOptions.stopBits !== undefined) {
      this.defaultOptions.stopBits = serialPortOptions.stopBits
    }
  }

  async requestSerialPort () {
    try {
      const port = await navigator.serial.requestPort()

      // Store as a JSON string in localStorage
      const printerPort = JSON.stringify(port.getInfo())
      window.localStorage.setItem(this.printerPortKey, printerPort)

      // console.log(`Stored USB Vendor ID: ${usbVendorId}, Product ID: ${usbProductId}`)

      return port
    } catch (error) {
      console.error('Failed to request serial port:', error)
    }
  }

  async getSavedSerialPort () {
    const printerPort = window.localStorage.getItem(this.printerPortKey)

    if (!printerPort) {
      // console.log('No saved printer port found.')
      return null
    }

    try {
      const { usbVendorId, usbProductId } = JSON.parse(printerPort)
      const ports = await navigator.serial.getPorts()

      for (const port of ports) {
        const portInfo = port.getInfo()
        if (portInfo.usbVendorId === usbVendorId && portInfo.usbProductId === usbProductId) {
          return port
        }
      }
    } catch (error) {
      console.error('Error parsing printer port data:', error)
    }

    return null
  }

  async open () {
    if (!navigator.serial) {
      throw new Error('Web Serial API is not supported in this browser.')
    }

    try {
      // this.port = await navigator.serial.requestPort()
      this.port = await this.getSavedSerialPort()

      if (!this.port) {
        this.port = await this.requestSerialPort()
      }

      if (this.port) {
        await this.port.open(this.defaultOptions)
        this.portOpened = true

        // Start reading data from the serial port
        this.reader = this.port.readable.getReader()
        this.readData(this.reader).catch(console.error)
      } else {
        throw new Error('Failed to open printer port')
      }
    } catch (error) {
      console.error('Error connecting to the printer:', error)
      throw error
    }
  }

  async close (config = {
    deleteSavedConnection: false
  }) {
    this.reader?.cancel()
    this.reader?.releaseLock()
    this.port?.close()

    this.port = null
    this.portOpened = false

    if (config.deleteSavedConnection) {
      window.localStorage.removeItem(this.printerPortKey)
    }
  }

  async readData (reader) {
    try {
      while (this.portOpened) {
        if (!this.portOpened) break

        const { value, done } = await reader.read()

        if (done || !this.portOpened) {
          // console.log('Reader closed, stopping read loop.')
          break
        }

        value.forEach(v => {
          if (v === 1) { // Start of a message
            this.messageStarted = true
            this.messageBytes = []
          }

          if (this.messageStarted) {
            this.messageBytes.push(v)
          }

          if (v === 3) { // End of a message
            this.messageStarted = false
            // console.log('Complete message received:', this.messageBytes)

            if (this.commandPromise) {
              const parsedResponse = this.parseResponse(this.messageBytes)
              // console.log('Parsed response:', parsedResponse)
              this.commandPromise.resolve(parsedResponse)
              this.messageBytes = []
            }
          }
        })
      }
    } catch (error) {
      if (!this.portOpened) {
        console.log('Reading stopped because port was closed.')
      } else {
        console.error('Error reading serial data:', error)
        this.portOpened = false
      }
    } finally {
      if (this.reader) {
        this.reader.releaseLock()
        this.reader = null
      }
    }
  }

  defer () {
    let resolve, reject
    const promise = new Promise((_resolve, _reject) => {
      resolve = _resolve
      reject = _reject
    })

    return { resolve, reject, promise }
  }

  generateCommandPack (cmd, data = null) {
    const PREAMBLE = 0x01
    const POSTAMBLE = 0x05
    const TERMINATOR = 0x03
    const dataLength = data ? data.length : 0
    const len = dataLength + 5 + 0x20 - 1
    data = data ?? []
    const seq = this.getRandomSequenceByte()
    const bcc = this.getPackageSum(PREAMBLE, len, seq, cmd, data, POSTAMBLE)

    return new Uint8Array([
      PREAMBLE,
      len,
      seq,
      cmd,
      ...data,
      POSTAMBLE,
      ...bcc,
      TERMINATOR
    ])
  }

  getPackageSum (preamble, len, seq, cmd, data, postamble) {
    let sum = preamble + len + seq + cmd + postamble

    for (const byte of data) {
      sum += byte
    }

    sum--

    const hexSum = sum.toString(16).padStart(4, '0')
    const result = []

    for (let i = 0; i < hexSum.length; i++) {
      const subSumString = `3${hexSum[i]}`
      const subSum = parseInt(subSumString, 16)
      result.push(subSum)
    }

    return result
  }

  getRandomSequenceByte () {
    return Math.floor(Math.random() * (0xFF - 0x20 + 1)) + 0x20
  }

  async runCommand (cmd, data = null) {
    this.commandPromise = this.defer()

    if (data) {
      data = this.encodeWindows1251(data)
    }

    const pack = this.generateCommandPack(cmd, data)
    const writer = this.port.writable.getWriter()
    await writer.write(pack)
    writer.releaseLock()
    // return this.commandPromise.promise

    const timeout = setTimeout(() => {
      console.error('Timeout: No response received')
      this.commandPromise.reject(new Error('No response from device'))
    }, 5000)

    try {
      const response = await this.commandPromise.promise
      clearTimeout(timeout) // Clear timeout if response is received
      return response
    } catch (error) {
      clearTimeout(timeout)
      throw error
    }
  }

  getStatusCodes () {
    return new Map([
      ['GENERAL_ERROR', 1],
      ['NOT_USED', 2],
      ['CLOCK_NOT_SYNCED', 3],
      ['COMMAND_CODE_INVALID', 4],
      ['SYNTAX_ERROR', 5],
      ['RESERVED', 6],
      ['PRINTER_COVER_OPENED', 7],
      ['COMMAND_CANNOT_BE_EXECUTED_IN_CURRENT_MODE', 8],
      ['COMMAND_EXECUTION_CAUSED_OVERFLOW_IN_AMOUNT_FIELDS', 9],
      ['NON_FISCAL_RECEIPT_OPENED', 10],
      ['NOT_ENOUGH_JOURNAL_PAPER', 11],
      ['FISCAL_RECEIPT_OPENED', 12],
      ['NO_PAPER', 13],
      ['OR_ALL_ERRORS', 14],
      ['FISCAL_MEMORY_FULL', 15],
      ['MEMORY_FOR_LESS_THAN_40_RECORDS_LEFT', 16],
      ['ID_FOR_FISCAL_DEVICE_AND_FISCAL_MEMORY_IS_SET', 17],
      ['TAX_NUMBER_SET', 18],
      ['FISCAL_MEMORY_SAVE_ERROR', 19],
      ['TAX_RATES_ENTERED_AT_LEAST_ONCE', 20],
      ['DEVICE_IS_IN_FISCAL_MODE', 21],
      ['FISCAL_MEMORY_IS_FORMATTED', 22],
      ['JOURNAL_PAPER_END', 23],
      ['NOT_ENOUGH_PAPER', 24],
      ['PRINTER_FAILURE', 25]
    ])
  }

  parseResponse (response) {
    const lengthBytes = response[1] - 32
    const dataResponse = []

    if (lengthBytes > 11) {
      for (let k = 4; k <= (lengthBytes - 8); k++) {
        dataResponse.push(response[k])
      }
    }

    const d = new TextDecoder().decode(new Uint8Array(dataResponse))

    const statusBytes = []
    let statusI = 0
    let statusStart = false

    response.forEach(d => {
      if (d === 4) {
        statusStart = true
      }

      if (d === 5) {
        statusStart = false
      }

      if (statusStart && statusI > 0) {
        statusBytes.push(d)
      }

      if (statusStart) {
        statusI++
      }
    })

    const statusMessages = []
    const errorMessages = []
    const responseStatusCodes = []

    statusBytes.forEach((v, i) => {
      const binaryResponse = parseInt(v).toString(2)
      const bitsReversed = this.reverseString(binaryResponse)

      if (i === 0) {
        if (bitsReversed[5] === '1') {
          statusMessages.push('Општа грешка')
          errorMessages.push('Општа грешка')
          responseStatusCodes.push(this.statusCodes.get('GENERAL_ERROR'))
        }

        if (bitsReversed[4] === '1') {
          statusMessages.push('Механизмот за печатење е неисправен')
          errorMessages.push('Механизмот за печатење е неисправен')
          responseStatusCodes.push(this.statusCodes.get('PRINTER_FAILURE'))
        }

        if (bitsReversed[3] === '1') {
          statusMessages.push('Не се користи')
          responseStatusCodes.push(this.statusCodes.get('NOT_USED'))
        }

        if (bitsReversed[2] === '1') {
          statusMessages.push('Часовникот не е подесен')
          errorMessages.push('Часовникот не е подесен')
          responseStatusCodes.push(this.statusCodes.get('CLOCK_NOT_SYNCED'))
        }

        if (bitsReversed[1] === '1') {
          statusMessages.push('Кодот на примената наредба е невалиден')
          errorMessages.push('Кодот на примената наредба е невалиден')
          responseStatusCodes.push(this.statusCodes.get('COMMAND_CODE_INVALID'))
        }

        if (bitsReversed[0] === '1') {
          statusMessages.push('Зададените податоци имаат синтаксна грешка')
          errorMessages.push('Зададените податоци имаат синтаксна грешка')
          responseStatusCodes.push(this.statusCodes.get('SYNTAX_ERROR'))
        }
      } else if (i === 1) {
        if (bitsReversed[6] === '1') {
          statusMessages.push('Резервиран')
          responseStatusCodes.push(this.statusCodes.get('RESERVED'))
        }

        if (bitsReversed[5] === '1') {
          statusMessages.push('Капакот на принтерот е отворен')
          errorMessages.push('Капакот на принтерот е отворен')
          responseStatusCodes.push(this.statusCodes.get('PRINTER_COVER_OPENED'))
        }

        if (bitsReversed[1] === '1') {
          statusMessages.push('Извршување на наредбата не е дозволено во тековниот режим на принтерот')
          errorMessages.push('Извршување на наредбата не е дозволено во тековниот режим на принтерот')
          responseStatusCodes.push(this.statusCodes.get('COMMAND_CANNOT_BE_EXECUTED_IN_CURRENT_MODE'))
        }

        if (bitsReversed[0] === '1') {
          statusMessages.push('При извршување на наредбата, сумите предизвикаат преполнување на некои од полињата')
          errorMessages.push('При извршување на наредбата, сумите предизвикаат преполнување на некои од полињата')
          responseStatusCodes.push(this.statusCodes.get('COMMAND_EXECUTION_CAUSED_OVERFLOW_IN_AMOUNT_FIELDS'))
        }
      } else if (i === 2) {
        if (bitsReversed[5] === '1') {
          statusMessages.push('Резервиран')
          responseStatusCodes.push(this.statusCodes.get('NON_FISCAL_RECEIPT_OPENED'))
        }

        if (bitsReversed[4] === '1') {
          statusMessages.push('Хартијата на контролната лента е при крај')
          responseStatusCodes.push(this.statusCodes.get('NOT_ENOUGH_JOURNAL_PAPER'))
        }

        if (bitsReversed[3] === '1') {
          statusMessages.push('Отворена фискална или сторна сметка')
          responseStatusCodes.push(this.statusCodes.get('FISCAL_RECEIPT_OPENED'))
        }

        if (bitsReversed[2] === '1') {
          statusMessages.push('Нема хартија за контролната лента')
          responseStatusCodes.push(this.statusCodes.get('JOURNAL_PAPER_END'))
        }

        if (bitsReversed[1] === '1') {
          statusMessages.push('Хартијата е при крај. Заеднички сигнал за двата принтери – за сметковна и контролна лента')
          responseStatusCodes.push(this.statusCodes.get('NOT_ENOUGH_PAPER'))
        }

        if (bitsReversed[0] === '1') {
          statusMessages.push('Нема хартија. Заеднички сигнал за двата принтери – за сметковна и контролна лента. Ако се случи ова за време на наредба, поврзана со печатење, наредбата не се извршува и не се менува состојбата на принтерот')
          responseStatusCodes.push(this.statusCodes.get('NO_PAPER'))
        }
      } else if (i === 4) {
        if (bitsReversed[5] === '1') {
          statusMessages.push('OR на сите грешки, означени со * од bytes 4 и 5')
          responseStatusCodes.push(this.statusCodes.get('OR_ALL_ERRORS'))
        }

        if (bitsReversed[4] === '1') {
          statusMessages.push('Фискалната меморија е полна')
          responseStatusCodes.push(this.statusCodes.get('FISCAL_MEMORY_FULL'))
        }

        if (bitsReversed[3] === '1') {
          statusMessages.push('Место за помалку од 40 записи во фискалната меморија')
          responseStatusCodes.push(this.statusCodes.get('MEMORY_FOR_LESS_THAN_40_RECORDS_LEFT'))
        }

        if (bitsReversed[2] === '1') {
          statusMessages.push('Нема модул на фискална меморија')
          responseStatusCodes.push(this.statusCodes.get('ID_FOR_FISCAL_DEVICE_AND_FISCAL_MEMORY_IS_SET'))
        }

        if (bitsReversed[1] === '1') {
          statusMessages.push('Не се користи')
          responseStatusCodes.push(this.statusCodes.get('TAX_NUMBER_SET'))
        }

        if (bitsReversed[0] === '1') {
          statusMessages.push('Грешка при запис во фискалната меморија')
          responseStatusCodes.push(this.statusCodes.get('FISCAL_MEMORY_SAVE_ERROR'))
        }
      } else if (i === 5) {
        if (bitsReversed[4] === '1') {
          statusMessages.push('Зададена е барем една даночна стапка')
          responseStatusCodes.push(this.statusCodes.get('TAX_RATES_ENTERED_AT_LEAST_ONCE'))
        }

        if (bitsReversed[3] === '1') {
          statusMessages.push('Принтерот е фискализиран')
          responseStatusCodes.push(this.statusCodes.get('DEVICE_IS_IN_FISCAL_MODE'))
        }

        if (bitsReversed[1] === '1') {
          statusMessages.push('Фискалната меморија е форматирана')
          responseStatusCodes.push(this.statusCodes.get('FISCAL_MEMORY_IS_FORMATTED'))
        }
      }
    })

    return {
      hexResponse: response,
      messages: statusMessages,
      errors: errorMessages,
      responseStatusCodes,
      data: d
    }
  }

  reverseString (str) {
    return str.split('').reverse().join('')
  }

  encodeWindows1251 (str) {
    return encode(str)
  }
}

export default SerialWrapper
